import React, {useEffect, useState} from "react";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {useFormik} from "formik";
import {Button, Col, Form, Modal, Popconfirm, Row, Switch} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../../hooks/useStore";
import {HideableInput} from "../../../../_defaultComponents/HideableInput/HideableInput";
export const MealCardIntegrationUpdateModal = ({
    subcompanyId,
    selectedIntegration,
    visibility,
    onCancel,
    integrationsFields = []
}) => {
    const {t} = useTranslation(["integrations"]);
    const stores = useStore();
    const [initialValues, setInitialValues] = useState({});
    const [validation, setValidation] = useState(
        yup.object().shape({
            mealCard: yup
                .boolean()
                .required(t("integrations.validations.platformRequired", {ns: "integrations"}))
        })
    );
    const [confirmLoading, setConfirmLoading] = useState();

    const formik = useFormik({
        initialValues: {
            isActive: Boolean(selectedIntegration?.isActive),
            mealCard: "",
            ...initialValues
        },
        validationSchema: validation,
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const subCompanyId = subcompanyId;
                const {mealCard, ...integration} = values;

                await stores.companyStore.updateSubcompanyMealCardIntegration({
                    subCompanyId,
                    mealCard,
                    integration
                });

                onCancel(true);
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    const {values, errors, touched, handleSubmit, setFieldValue, resetForm} = formik;

    const generateInitialValuesByType = () => {
        const initialValues = {mealCard: selectedIntegration?.mealCard};
        integrationsFields.forEach(integration => {
            if (integration.mealCard === selectedIntegration?.mealCard) {
                integration.exclusiveFields.forEach(exclusiveField => {
                    initialValues[exclusiveField] = selectedIntegration?.[exclusiveField] || "";
                });
            }
        });
        return initialValues;
    };

    const generateValidationByType = () => {
        const validation = {};
        integrationsFields.forEach(integration => {
            if (integration.mealCard === selectedIntegration?.mealCard) {
                integration.exclusiveFields.forEach(exclusiveField => {
                    validation[exclusiveField] = yup
                        .string()
                        .required(t("validation.required", {ns: "common"}));
                });
            }
        });
        return yup.object().shape(validation);
    };

    const reset = () => {
        setInitialValues({});
        resetForm();
        setValidation(
            yup.object().shape({
                mealCard: yup
                    .boolean()
                    .required(t("integrations.validations.platformRequired", {ns: "integrations"}))
            })
        );
    };

    const handleChangeFlags = (field, checked) => {
        switch (field) {
            case "isActive":
                handleIsActiveChange(checked);
                break;
        }
    };

    const handleIsActiveChange = checked => {
        setFieldValue("isActive", checked);
    };

    const handleRemove = async () => {
        setConfirmLoading(true);
        try {
            const subCompanyId = subcompanyId;
            const {mealCard} = values;

            await stores.companyStore.deleteSubcompanyMealCardIntegration({
                subCompanyId,
                mealCard
            });
            onCancel(true);
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            setConfirmLoading(false);
        }
    };
    useEffect(() => reset(), [visibility]);
    useEffect(() => {
        if (selectedIntegration?.mealCard) {
            setInitialValues(generateInitialValuesByType());
            setValidation(generateValidationByType());
        }
    }, [selectedIntegration?.mealCard]);

    if (!selectedIntegration?.mealCard) return null;
    return (
        <Modal
            title={t("integrations.modals.updateMealCardModal.title", {
                ns: "integrations",
                mealCard: t(`integrations.mealCards.${selectedIntegration?.mealCard}.name`, {
                    ns: "integrations"
                })
            })}
            visible={visibility}
            onCancel={() => {
                onCancel(false);
            }}
            footer={
                <Row justify="space-between">
                    <Row justify="start">
                        <Col>
                            <Popconfirm
                                title={t("modal.confirmTitle", {ns: "common"})}
                                open={open}
                                onConfirm={handleRemove}
                                onCancel={() => setOpen(false)}
                                okText={t("modal.yesButton", {ns: "common"})}
                                cancelText={t("modal.noButton", {ns: "common"})}
                                disabled={confirmLoading || !values.mealCard}
                            >
                                <Button
                                    type="danger"
                                    loading={confirmLoading}
                                    disabled={confirmLoading || !values.mealCard}
                                >
                                    {t("actions.delete", {ns: "common"})}
                                </Button>
                            </Popconfirm>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Col>
                            <Button
                                onClick={() => {
                                    onCancel(false);
                                }}
                                loading={confirmLoading}
                            >
                                {t("modal.cancelButton2", {ns: "common"})}
                            </Button>
                            <Popconfirm
                                title={t("modal.confirmTitle", {ns: "common"})}
                                open={open}
                                onConfirm={handleSubmit}
                                onCancel={() => setOpen(false)}
                                okText={t("modal.yesButton", {ns: "common"})}
                                cancelText={t("modal.noButton", {ns: "common"})}
                                disabled={!values.mealCard}
                            >
                                <Button
                                    type="primary"
                                    disabled={!values.mealCard}
                                    loading={confirmLoading}
                                >
                                    {t("actions.update", {ns: "common"})}
                                </Button>
                            </Popconfirm>
                        </Col>
                    </Row>
                </Row>
            }
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    {integrationsFields.map(
                        integration =>
                            integration.mealCard === values.mealCard &&
                            integration.exclusiveFields.map(exclusiveField => (
                                <Col span={24} key={`${integration.mealCard}.${exclusiveField}`}>
                                    <Form.Item
                                        validateStatus={
                                            errors[exclusiveField] && touched[exclusiveField]
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors[exclusiveField] && touched[exclusiveField]
                                                ? errors[exclusiveField]
                                                : null
                                        }
                                    >
                                        <h4>
                                            {t(
                                                `integrations.mealCards.${integration.mealCard}.fields.${exclusiveField}`,
                                                {
                                                    ns: "integrations"
                                                }
                                            )}
                                        </h4>
                                        <HideableInput
                                            defaultHidden={true}
                                            value={values[exclusiveField]}
                                            onChange={value => {
                                                setFieldValue(exclusiveField, value);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            ))
                    )}
                    {["isActive"].map(field => (
                        <Col span={24} key={field}>
                            <Form.Item
                                validateStatus={
                                    errors[field] && touched[field] ? "error" : "success"
                                }
                                help={errors[field] && touched[field] ? errors[field] : null}
                            >
                                <h4>
                                    {" "}
                                    {t(`integrations.commonFields.${field}`, {
                                        ns: "integrations"
                                    })}
                                </h4>
                                <Switch
                                    checked={values[field]}
                                    checkedChildren={t("other.active", {ns: "common"})}
                                    unCheckedChildren={t("other.passive", {ns: "common"})}
                                    data-testid={field}
                                    onChange={checked => handleChangeFlags(field, checked)}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </Form>
        </Modal>
    );
};
